import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";

/**
 * components/MoveSalespersonModal.js
 * The ONE action for relocating a role that has real transaction history (Stage 5 — roles
 * pinned to their location; see utils/salespeople.js's own header comment and
 * `moveSalespersonBranch`). A role with any transactions can never have its own `branchId`
 * edited directly — `updateSalesperson` refuses that write outright — because its lifetime
 * transaction count would otherwise read as a count of wherever it sits TODAY, not what those
 * transactions are actually about. The only way to relocate someone with real history is to
 * deactivate their current role and pick up a role at the destination — reactivating one that's
 * already there (inactive), or starting fresh at zero — as ONE atomic action, not two separate
 * clicks, since the user would always do both together anyway.
 *
 * **The confirmation states BOTH halves before acting, in the SAME screen** — never a two-step
 * popup, and never revealed only after confirming. Both the title and the body text update live
 * as the destination is picked, so the exact consequence is visible before "Pindahkan" is ever
 * clickable.
 *
 * Props: `{ personName, currentBranchLabel, transactionCount, destinations, onConfirm,
 * onClose }`. `destinations` is caller-pre-filtered via `utils/salespeople.js`'s
 * `pickableBranchesForPerson(..., "move")`, with the role's own current branch ALSO excluded by
 * the caller — a branch the person already holds an ACTIVE role at is never offered here at all
 * (refused, not something this modal has to explain); a branch they hold an INACTIVE role at IS
 * offered, `willReactivate: true`, since picking it reconnects that old role's own transaction
 * history instead of starting a new one at zero. `onConfirm(newBranchId)` is called once, on the
 * final click — this component holds no intermediate "are you sure" step of its own.
 */
export default function MoveSalespersonModal({
  personName, currentBranchLabel, transactionCount, destinations, onConfirm, onClose,
}) {
  // Silent-defaults fix (2026-10-12): no destination pre-picked — "Pindahkan" waits for a real
  // choice (it used to start on the first branch in the list).
  const [choiceKey, setChoiceKey] = useState("");
  useModalEsc(onClose);

  const chosen = destinations.find((d) => destKey(d) === choiceKey);
  const message = chosen && (chosen.willReactivate
    ? `Peran ${personName} di ${currentBranchLabel} akan dinonaktifkan — ${txnPhrase(transactionCount)} tetap tercatat di ${currentBranchLabel}. ${chosen.label} sudah pernah jadi tempat ${personName} sebelumnya (nonaktif) — peran itu akan diaktifkan lagi, menyambungkan transaksi lamanya di sana.`
    : `Peran ${personName} di ${currentBranchLabel} akan dinonaktifkan — ${txnPhrase(transactionCount)} tetap tercatat di ${currentBranchLabel}. Peran baru di ${chosen.label} dibuat mulai dari nol.`);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Pindahkan {personName}{chosen ? ` ke ${chosen.label}` : ""}?</h2>
        {destinations.length === 0 ? (
          <div className="page-sub">
            {personName} sudah punya peran aktif di setiap lokasi lain — tidak ada tujuan yang bisa dipilih.
          </div>
        ) : (
          <>
            <div className="form-row">
              <label className="form-label" htmlFor="move-sales-pindah-ke">Pindah ke</label>
              <select id="move-sales-pindah-ke" className="form-select" value={choiceKey} onChange={(e) => setChoiceKey(e.target.value)}>
                {!choiceKey && <option value="" disabled>— pilih tujuan —</option>}
                {destinations.map((d) => (
                  <option key={destKey(d)} value={destKey(d)}>
                    {d.label}{d.willReactivate ? " (aktifkan kembali)" : ""}
                  </option>
                ))}
              </select>
            </div>
            {message && <div className="page-sub">{message}</div>}
          </>
        )}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="button" className="btn btn-primary" disabled={!chosen}
            onClick={() => onConfirm(chosen.id)}>
            Pindahkan
          </button>
        </div>
      </div>
    </div>
  );
}

function destKey(d) {
  return d.id === null ? "__pusat__" : d.id;
}
function txnPhrase(n) {
  return n === 1 ? "1 transaksinya" : `${n} transaksinya`;
}
